import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import UnifiedPortalHeader from "@/components/UnifiedPortalHeader";
import SharedFooter from "@/components/SharedFooter";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import {
  Trophy,
  Medal,
  Crown,
  Star,
  Target,
  Flame,
  Users,
  TrendingUp,
  Award,
  Zap,
  ChevronUp,
  BarChart3,
  GraduationCap,
  Lock,
  User,
  AlertCircle,
} from "lucide-react";

// Tier colors and styles
const tierStyles = {
  elite: {
    bg: "bg-gradient-to-r from-yellow-400 via-amber-500 to-yellow-600",
    border: "border-yellow-400",
    text: "text-yellow-600",
    icon: Crown,
    glow: "shadow-lg shadow-yellow-400/30"
  },
  excellent: {
    bg: "bg-gradient-to-r from-purple-500 to-violet-600",
    border: "border-purple-400",
    text: "text-purple-600",
    icon: Trophy,
    glow: "shadow-lg shadow-purple-400/30"
  },
  great: {
    bg: "bg-gradient-to-r from-blue-500 to-cyan-600",
    border: "border-blue-400",
    text: "text-blue-600",
    icon: Medal,
    glow: "shadow-lg shadow-blue-400/30"
  },
  good: {
    bg: "bg-gradient-to-r from-emerald-500 to-green-600",
    border: "border-emerald-400",
    text: "text-emerald-600",
    icon: Star,
    glow: "shadow-lg shadow-emerald-400/30"
  },
  average: {
    bg: "bg-gradient-to-r from-teal-500 to-cyan-600",
    border: "border-teal-400",
    text: "text-teal-600",
    icon: Target,
    glow: "shadow-lg shadow-teal-400/30"
  },
  improving: {
    bg: "bg-gradient-to-r from-orange-500 to-amber-600",
    border: "border-orange-400",
    text: "text-orange-600",
    icon: TrendingUp,
    glow: "shadow-lg shadow-orange-400/30"
  }
};

export default function Leaderboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [standingData, setStandingData] = useState(null);
  const [featureLocked, setFeatureLocked] = useState(false);
  const [upgradeMessage, setUpgradeMessage] = useState("");
  
  // Check feature flag for gamification
  const { isEnabled: isGamificationEnabled, isLoading: flagLoading } = useFeatureFlag(
    'student_gamification',
    { role: 'student' }
  );

  const fetchStanding = useCallback(async () => {
    try {
      const response = await fetch(`${API}/leaderboard/standing`, {
        credentials: "include"
      });
      
      if (response.ok) {
        const data = await response.json();
        setStandingData(data);
      } else if (response.status === 403) {
        // Feature locked (either subscription or feature flag)
        const data = await response.json();
        setFeatureLocked(true);
        setUpgradeMessage(data.detail?.message || data.detail || "Leaderboard is available on Plus and Pro plans");
      } else if (response.status === 401) {
        navigate("/");
      } else {
        toast.error("Failed to load leaderboard");
      }
    } catch (error) {
      toast.error("Failed to load leaderboard");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    // Only fetch if feature flag is enabled
    if (!flagLoading && isGamificationEnabled) {
      fetchStanding();
    } else if (!flagLoading && !isGamificationEnabled) {
      setFeatureLocked(true);
      setUpgradeMessage("Leaderboard feature is currently unavailable. Please check back later!");
      setLoading(false);
    }
  }, [fetchStanding, flagLoading, isGamificationEnabled]);

  if (loading || flagLoading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin"></div>
          <p className="text-gray-600">Loading leaderboard...</p>
        </div>
      </div>
    );
  }

  // Feature locked view
  if (featureLocked) {
    return (
      <div className="min-h-screen bg-slate-100 flex flex-col">
        <UnifiedPortalHeader
          portalType="student"
          userName=""
          userSubtitle=""
          onLogout={() => navigate("/student/profile")}
          showUserInfo={false}
          showHomeButton={false}
          hideLogout={true}
        />
        <main className="max-w-4xl mx-auto px-4 py-8 flex-1">
          <Card className="border-0 shadow-lg">
            <CardContent className="pt-8 pb-8 text-center">
              <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Lock className="w-10 h-10 text-yellow-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Premium Feature
              </h2>
              <p className="text-gray-600 mb-6 max-w-md mx-auto">
                {upgradeMessage}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  className="bg-gradient-to-r from-yellow-500 to-orange-500"
                  onClick={() => navigate("/parent/dashboard")}
                  data-testid="upgrade-btn"
                >
                  <Trophy className="w-4 h-4 mr-2" />
                  Upgrade Plan
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate("/student/profile")}
                >
                  <User className="w-4 h-4 mr-2" />
                  Back to Profile
                </Button>
              </div>
            </CardContent>
          </Card>
        </main>
        <SharedFooter />
      </div>
    );
  }

  if (!standingData) {
    return (
      <div className="min-h-screen bg-slate-100 flex flex-col">
        <UnifiedPortalHeader
          portalType="student"
          userName=""
          userSubtitle=""
          onLogout={() => navigate("/student/profile")}
          showUserInfo={false}
          showHomeButton={false}
          hideLogout={true}
        />
        <main className="flex-1 flex items-center justify-center">
          <Card className="max-w-md">
            <CardContent className="pt-6 text-center">
              <Trophy className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h2 className="text-xl font-bold mb-2">No Data Yet</h2>
              <p className="text-gray-500 mb-4">Start learning to see your ranking!</p>
              <Button onClick={() => navigate("/student/learn")}>
                Start Learning
              </Button>
            </CardContent>
          </Card>
        </main>
        <SharedFooter />
      </div>
    );
  }

  const { standing, student_stats, class_stats, top_performers, has_percentile_access } = standingData;
  
  // Default tier for users without percentile access
  const defaultTier = {
    bg: "bg-gradient-to-r from-emerald-500 to-teal-600",
    border: "border-emerald-400",
    text: "text-emerald-600",
    icon: Star,
    glow: "shadow-lg shadow-emerald-400/30"
  };
  
  const tier = standing.percentile_info ? (tierStyles[standing.percentile_info.tier] || tierStyles.improving) : defaultTier;
  const TierIcon = tier.icon;

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col">
      <UnifiedPortalHeader
        portalType="student"
        userName=""
        userSubtitle=""
        onLogout={() => navigate("/student/profile")}
        showUserInfo={false}
        showHomeButton={false}
        hideLogout={true}
      />

      <main className="max-w-6xl mx-auto px-4 py-6 space-y-6 flex-1">
        {/* Main Grid Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column - Your Standing */}
          <Card className={`border-2 ${tier.border} ${tier.glow} overflow-hidden`}>
            <div className={`${tier.bg} p-6 text-white`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-sm font-medium mb-1">Your Standing</p>
                  <div className="flex items-center gap-3">
                    <span className="text-5xl font-bold">#{standing.rank}</span>
                    <div className="flex flex-col">
                      {has_percentile_access && standing.percentile_info ? (
                        <>
                          <span className="text-lg font-semibold">{standing.percentile_info.label}</span>
                          <span className="text-white/70 text-sm">of {standing.total_students} students</span>
                        </>
                      ) : (
                        <>
                          <span className="text-lg font-semibold">Rank #{standing.rank}</span>
                          <span className="text-white/70 text-sm">of {standing.total_students} students</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <div className="w-20 h-20 bg-white/20 rounded-2xl flex items-center justify-center">
                  <TierIcon className="w-10 h-10" />
                </div>
              </div>
              {has_percentile_access && standing.percentile_info ? (
                <p className="mt-4 text-white/90 text-sm">{standing.percentile_info.description}</p>
              ) : (
                <p className="mt-4 text-white/90 text-sm">Keep learning to improve your ranking!</p>
              )}
            </div>
            
            <CardContent className="pt-4">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="text-center p-3 bg-emerald-50 rounded-lg border border-emerald-100">
                  <Target className="h-5 w-5 text-emerald-600 mx-auto mb-1" />
                  <p className="text-2xl font-bold text-gray-900">{student_stats.total_questions}</p>
                  <p className="text-xs text-gray-500">Questions</p>
                </div>
                <div className="text-center p-3 bg-blue-50 rounded-lg border border-blue-100">
                  <Zap className="h-5 w-5 text-blue-600 mx-auto mb-1" />
                  <p className="text-2xl font-bold text-gray-900">{student_stats.accuracy}%</p>
                  <p className="text-xs text-gray-500">Accuracy</p>
                </div>
                <div className="text-center p-3 bg-orange-50 rounded-lg border border-orange-100">
                  <Flame className="h-5 w-5 text-orange-500 mx-auto mb-1" />
                  <p className="text-2xl font-bold text-gray-900">{student_stats.current_streak}</p>
                  <p className="text-xs text-gray-500">Current Streak</p>
                </div>
                <div className="text-center p-3 bg-purple-50 rounded-lg border border-purple-100">
                  <Award className="h-5 w-5 text-purple-600 mx-auto mb-1" />
                  <p className="text-2xl font-bold text-gray-900">{Math.round(standing.score)}</p>
                  <p className="text-xs text-gray-500">Score</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Right Column - Class Stats */}
          {class_stats && (
            <Card className="h-fit">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-emerald-600" />
                  Class Statistics
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-gray-500 text-sm">Avg Score</p>
                    <p className="text-3xl font-bold text-gray-900">{class_stats.average_score}</p>
                    <p className={`text-xs mt-1 ${standing.score > class_stats.average_score ? 'text-green-600' : 'text-gray-500'}`}>
                      {standing.score > class_stats.average_score ? `+${(standing.score - class_stats.average_score).toFixed(1)} above avg` : 'Keep improving!'}
                    </p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-gray-500 text-sm">Highest Score</p>
                    <p className="text-3xl font-bold text-gray-900">{class_stats.highest_score}</p>
                    <p className="text-xs text-gray-500 mt-1">Top performer</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-gray-500 text-sm">Avg Questions</p>
                    <p className="text-3xl font-bold text-gray-900">{Math.round(class_stats.average_questions)}</p>
                    <p className={`text-xs mt-1 ${student_stats.total_questions > class_stats.average_questions ? 'text-green-600' : 'text-gray-500'}`}>
                      {student_stats.total_questions > class_stats.average_questions ? 'Above average!' : 'Practice more'}
                    </p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-gray-500 text-sm">Avg Accuracy</p>
                    <p className="text-3xl font-bold text-gray-900">{class_stats.average_accuracy}%</p>
                    <p className={`text-xs mt-1 ${student_stats.accuracy > class_stats.average_accuracy ? 'text-green-600' : 'text-gray-500'}`}>
                      {student_stats.accuracy > class_stats.average_accuracy ? 'Above average!' : 'Keep learning'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Top Performers - Full Width */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Trophy className="h-5 w-5 text-yellow-500" />
              Top Performers - Grade {standing.grade}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {top_performers && top_performers.length > 0 ? (
              <div className="space-y-2">
                {top_performers.map((performer) => (
                  <div 
                    key={performer.student_id || performer.id || `performer-${performer.rank}`}
                    className={`flex items-center gap-3 p-3 rounded-lg transition-all ${
                      performer.is_current_user 
                        ? 'bg-emerald-50 border-2 border-emerald-500' 
                        : 'bg-gray-50 hover:bg-gray-100'
                    }`}
                  >
                    {/* Rank Badge */}
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                      performer.rank === 1 ? 'bg-yellow-400 text-yellow-900' :
                      performer.rank === 2 ? 'bg-gray-300 text-gray-700' :
                      performer.rank === 3 ? 'bg-amber-600 text-white' :
                      'bg-gray-200 text-gray-600'
                    }`}>
                      {performer.rank <= 3 ? (
                        performer.rank === 1 ? <Crown className="w-5 h-5" /> :
                        performer.rank === 2 ? <Medal className="w-5 h-5" /> :
                        <Award className="w-5 h-5" />
                      ) : (
                        <span>{performer.rank}</span>
                      )}
                    </div>
                    
                    {/* Name and Stats */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className={`font-semibold truncate ${performer.is_current_user ? 'text-emerald-700' : 'text-gray-900'}`}>
                          {performer.display_name}
                        </p>
                        {performer.is_current_user && (
                          <span className="px-2 py-0.5 bg-emerald-600 text-white text-xs rounded-full">You</span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-xs text-gray-500">
                        <span>{performer.questions_answered} questions</span>
                        <span>{performer.accuracy}% accuracy</span>
                        {performer.streak > 0 && (
                          <span className="flex items-center gap-1">
                            <Flame className="w-3 h-3 text-orange-500" />
                            {performer.streak}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {/* Score */}
                    <div className="text-right">
                      <p className="font-bold text-gray-900">{Math.round(performer.score)}</p>
                      <p className="text-xs text-gray-500">points</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <GraduationCap className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>No students have started learning yet</p>
                <p className="text-sm">Be the first to climb the leaderboard!</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Call to Action */}
        <Card className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white border-0">
          <CardContent className="py-6">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-center sm:text-left">
                <h3 className="text-xl font-bold mb-1">Ready to climb higher?</h3>
                <p className="text-emerald-100">Every question improves your ranking!</p>
              </div>
              <Button 
                onClick={() => navigate("/student/learn")}
                className="bg-white text-emerald-600 hover:bg-emerald-50 font-semibold px-6"
              >
                <Zap className="w-4 h-4 mr-2" />
                Start Learning
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Back to Profile Button */}
        <div className="flex justify-center pt-4">
          <Button
            className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-lg"
            onClick={() => navigate("/student/profile")}
          >
            <User className="w-4 h-4 mr-2" />
            Back to Profile
          </Button>
        </div>
      </main>

      <SharedFooter />
    </div>
  );
}
